import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { getUserToken, getRefreshToken, clearUserToken, setUserToken } from 'src/shared/utils/session'

const baseQuery = fetchBaseQuery({
  baseUrl: '/api/v2/storefront',
  prepareHeaders: (headers) => {
    const token = getUserToken()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  },
})

const baseQueryWithRefresh = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)

  if (result.error?.status === 401) {
    const refreshToken = getRefreshToken()

    if (refreshToken) {
      const refreshResult = await fetch('/spree_oauth/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
      })

      if (refreshResult.ok) {
        const refreshed = await refreshResult.json()
        setUserToken(refreshed.access_token, refreshed.refresh_token, refreshed.expires_in)
        result = await baseQuery(args, api, extraOptions)
      } else {
        clearUserToken()
      }
    } else {
      clearUserToken()
    }
  }

  return result
}

export const sessionApi = createApi({
  reducerPath: 'sessionApi',
  baseQuery: baseQueryWithRefresh,
  tagTypes: ['Session'],
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const token = getUserToken()

        if (!token) {
          return { data: null }
        }

        const result = await fetchWithBQ('current_user')

        if (result.error?.status === 401 || result.error?.status === 'FETCH_ERROR') {
          clearUserToken()
        }

        return result
      },
      providesTags: ['Session']
    }),
    logout: builder.mutation({
      queryFn: async () => {
        clearUserToken()
        return { data: null }
      }
    }),
  }),
})

export const {
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useLogoutMutation,
} = sessionApi